/* eslint-disable react/require-default-props */
import * as React from 'react';
import { ErrorMessage, Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { Customer, LoadedUser } from '../../interfaces';
import './form.css';
import {
  CreateCustomerValues,
  initFormValues,
  validationSchema,
} from '../../utils/form/customerForm';
import StyledPhoneInput from './PhoneInput';
import {
  addCustomerAlgolia,
  addCustomerDatabase,
  updateCustomer,
} from '../../services/user';
import TextareaInput from './TextareaInput';
import {
  invalidateCustomerOverview,
  isCustomerEditDisabled,
} from '../../services/customers';
import TextInput from './TextInput';

interface Props {
  loadedUser?: LoadedUser;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setShouldScrollToTop?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCustomer?: Customer;
}

export default function CustomerForm({
  loadedUser,
  setModalOpen,
  setLoading,
  setShouldScrollToTop,
  selectedCustomer,
}: Props) {
  const [formError, setFormError] = React.useState<any>('');
  const [loadedInitValues, setLoadedInitValues] =
    React.useState<CreateCustomerValues>({
      ...initFormValues,
    });

  const disableCustomerEdit =
    !!selectedCustomer && isCustomerEditDisabled(selectedCustomer.user);

  React.useEffect(() => {
    async function loadFormData() {
      if (selectedCustomer) {
        const tempNewInitValues: CreateCustomerValues = { ...initFormValues };

        if (selectedCustomer.user.firstName) {
          tempNewInitValues.firstName = selectedCustomer.user.firstName;
        }

        if (selectedCustomer.user.lastName) {
          tempNewInitValues.lastName = selectedCustomer.user.lastName;
        }

        if (selectedCustomer.user.email) {
          tempNewInitValues.email = selectedCustomer.user.email;
        }

        if (selectedCustomer.user.phoneNumber) {
          tempNewInitValues.phoneNumber = selectedCustomer.user.phoneNumber;
        }

        if (selectedCustomer.dob) {
          const dob = new Date(selectedCustomer.dob)
            .toISOString()
            .split('T')[0];
          tempNewInitValues.dob = dob;
        }

        if (selectedCustomer.internalNotes) {
          tempNewInitValues.internalNotes = selectedCustomer.internalNotes;
        }

        setLoadedInitValues(tempNewInitValues);
      } else {
        setLoadedInitValues(initFormValues);
      }
    }
    loadFormData();
  }, [selectedCustomer]);

  return (
    <Box
      style={{
        width: 900,
        margin: 'auto',
      }}>
      <Formik
        enableReinitialize
        initialValues={loadedInitValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          if (loadedUser) {
            let customerResponse;

            if (!selectedCustomer) {
              try {
                customerResponse = await addCustomerDatabase(
                  {
                    ...values,
                  },
                  loadedUser.business.id,
                );
              } catch (error) {
                if ((error as any).response.data.message) {
                  setFormError((error as any).response.data.message);
                } else if (error instanceof Error) {
                  setFormError(error.message);
                } else {
                  setFormError('An unexpected error occurred.'); // Some generic message
                }

                toast.error('Error creating customer');
                if (setShouldScrollToTop) {
                  setShouldScrollToTop(true);
                }

                setSubmitting(false); // End the submission process here
                return; // Exit the onSubmit function
              }
            } else {
              try {
                customerResponse = await updateCustomer(
                  {
                    ...values,
                  },
                  selectedCustomer.customerId,
                  loadedUser.business.id,
                );
              } catch (error) {
                if ((error as any).response.data.message) {
                  setFormError((error as any).response.data.message);
                } else if (error instanceof Error) {
                  setFormError(error.message);
                } else {
                  setFormError('An unexpected error occurred.'); // Some generic message
                }

                toast.error('Error updating customer');
                if (setShouldScrollToTop) {
                  setShouldScrollToTop(true);
                }

                setSubmitting(false); // End the submission process here
                return; // Exit the onSubmit function
              }
            }

            const customerId =
              customerResponse.customerId || customerResponse.userId;
            try {
              await addCustomerAlgolia(customerId, loadedUser.business.id);
            } catch (error) {
              // Do something with the error
            }

            toast.success('Customer successfully updated!');
            setModalOpen(false);
            setLoading(true);

            setSubmitting(false);

            await invalidateCustomerOverview(customerId);
          }
        }}>
        {({ isSubmitting }) => (
          <Form>
            {formError && (
              <Typography variant="body1" color="primary">
                {formError}
              </Typography>
            )}

            <div className="input-group">
              <Typography variant="h2">Customer Information</Typography>
              <div className="inputs">
                <div className="input-row">
                  <TextInput
                    label="First Name *"
                    name="firstName"
                    disabled={disableCustomerEdit}
                    placeholder="John"
                  />
                  <TextInput
                    label="Last Name"
                    name="lastName"
                    disabled={disableCustomerEdit}
                    placeholder="Doe"
                  />
                </div>

                <div className="input-row">
                  <StyledPhoneInput
                    placeholder="Phone Number"
                    name="phoneNumber"
                    label="Phone *"
                    search={false}
                    disabled={disableCustomerEdit}
                  />
                  <TextInput
                    label="Email"
                    name="email"
                    type="email"
                    placeholder="Email"
                    disabled={disableCustomerEdit}
                  />
                </div>

                <div className="input-row">
                  <TextInput
                    label="Birthday"
                    name="dob"
                    type="date"
                    disabled={disableCustomerEdit}
                  />
                </div>

                <div className="input-row">
                  <TextareaInput
                    name="internalNotes"
                    label="Internal Notes"
                    placeholder="Optional. Enter any notes for this customer. These notes are just for you and your team, and can not be viewed by the client."
                  />
                </div>
              </div>
              <div className="input-group-errors">
                <div className="error-message">
                  <ErrorMessage name="phoneNumber" />
                </div>
                <div className="error-message">
                  <ErrorMessage name="firstName" />
                </div>
                <div className="error-message">
                  <ErrorMessage name="lastName" />
                </div>
                <div className="error-message">
                  <ErrorMessage name="email" />
                </div>
                <div className="error-message">
                  <ErrorMessage name="dob" />
                </div>
                <div className="error-message">
                  <ErrorMessage name="internalNotes" />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              variant="contained"
              className="primary">
              {selectedCustomer ? 'Save Changes' : 'Add Customer'}
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
